import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CellDensityCalculator = () => {
  const [cellData, setCellData] = useState({
    cellCount: '',
    volume: '',
    dilutionFactor: '1'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCellData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateDensity = () => {
    const { cellCount, volume, dilutionFactor } = cellData;
    
    // Convert inputs to numbers
    const count = parseFloat(cellCount) || 0;
    const vol = parseFloat(volume) || 0;
    const dilution = parseFloat(dilutionFactor) || 1;
    
    if (count === 0 || vol === 0) {
      setError('Please enter valid numbers for cell count and volume');
      return;
    }

    // Calculate cell density
    const density = (count * dilution) / vol;
    const densityPerML = density * 1000; // Convert to cells/mL

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Cell count: ${count} cells`,
      `Volume: ${vol} µL`,
      `Dilution factor: ${dilution}`,
      '\nStep 2: Calculate actual cell count',
      `Actual cell count = Cell count × Dilution factor`,
      `Actual cell count = ${count} × ${dilution} = ${count * dilution} cells`,
      '\nStep 3: Calculate cell density',
      `Cell density = Actual cell count ÷ Volume`,
      `Cell density = ${count * dilution} ÷ ${vol} = ${density.toFixed(2)} cells/µL`,
      `Cell density = ${densityPerML.toFixed(2)} cells/mL`
    ];

    const newResults = {
      cellDensity: {
        perMicroLiter: density.toFixed(2),
        perMilliliter: densityPerML.toFixed(2)
      },
      actualCellCount: (count * dilution).toFixed(2),
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      labels: ['Cells/µL', 'Cells/mL'],
      datasets: [{
        label: 'Cell Density',
        data: [density, densityPerML],
        backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)'],
        borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)'],
        borderWidth: 1
      }]
    });
  };

  const exampleData = {
    cellCount: 150,
    volume: 10,
    dilutionFactor: 2,
    density: {
      perMicroLiter: 30,
      perMilliliter: 30000
    }
  };

  return (
    <>
      <Helmet>
        <title>Cell Density Calculator | BioLab Tools</title>
        <meta name="description" content="Accurate cell density calculator for biological research. Calculate cells/µL and cells/mL with dilution factor adjustment." />
        <meta name="keywords" content="cell density calculator, biology calculator, cell count calculator, cell density formula" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🦠</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Cell Density Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate cell density accurately using cell count, volume, and dilution factor. Get results in both cells/µL and cells/mL with step-by-step calculations.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cell Count</label>
                <input
                  type="number"
                  name="cellCount"
                  value={cellData.cellCount}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter number of cells counted"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Volume (µL)</label>
                <input
                  type="number"
                  name="volume"
                  value={cellData.volume}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter sample volume in microliters"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dilution Factor</label>
                <input
                  type="number"
                  name="dilutionFactor"
                  value={cellData.dilutionFactor}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter dilution factor (default: 1)"
                />
              </div>

              <button
                onClick={calculateDensity}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate Cell Density
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {results && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Cell Density</h3>
                      <div className="space-y-2">
                        <p className="text-lg font-medium">{results.cellDensity.perMicroLiter} cells/µL</p>
                        <p className="text-lg font-medium">{results.cellDensity.perMilliliter} cells/mL</p>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <h3 className="text-xl font-semibold mb-2">Actual Cell Count</h3>
                      <p className="text-lg font-medium">{results.actualCellCount} cells</p>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h3 className="text-xl font-semibold mb-4">Density Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Cell Density' }
                            },
                            x: { title: { display: true, text: 'Unit' } }
                          },
                          plugins: { title: { display: true, text: 'Cell Density by Unit' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <h3 className="text-xl font-semibold mb-4">Calculation Steps</h3>
                    <div className="space-y-4">
                      {results.calculationSteps.map((step, index) => (
                        <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                          <pre className="whitespace-pre-wrap break-words font-mono text-sm">
                            {step}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Example Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Example Calculation</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-600 mb-4">Here's an example calculation using sample cell count data:</p>
              
              <div className="space-y-6">
                <div className="p-6 bg-white rounded-lg shadow-sm">
                  <h4 className="font-semibold mb-2">Sample Data:</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Cell Count: {exampleData.cellCount} cells</li>
                    <li>Volume: {exampleData.volume} µL</li>
                    <li>Dilution Factor: {exampleData.dilutionFactor}</li>
                  </ul>
                </div>

                <div className="p-6 bg-white rounded-lg shadow-sm">
                  <h4 className="font-semibold mb-2">Calculations:</h4>
                  <ol className="list-decimal pl-6 space-y-4">
                    <li>
                      <p className="font-medium">Calculate actual cell count:</p>
                      <ul className="list-none pl-6 space-y-2">
                        <li>Actual cells = Cell count × Dilution factor</li>
                        <li>Actual cells = {exampleData.cellCount} × {exampleData.dilutionFactor} = {exampleData.cellCount * exampleData.dilutionFactor}</li>
                      </ul>
                    </li>
                    <li>
                      <p className="font-medium">Calculate cell density:</p>
                      <ul className="list-none pl-6 space-y-2">
                        <li>Density (cells/µL) = Actual cells ÷ Volume</li>
                        <li>Density = {exampleData.cellCount * exampleData.dilutionFactor} ÷ {exampleData.volume} = {exampleData.density.perMicroLiter} cells/µL</li>
                        <li>Density (cells/mL) = {exampleData.density.perMilliliter} cells/mL</li>
                      </ul>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Guide */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use Cell Density Calculator</h2>
            <p className="text-gray-600 mb-6">Follow these steps to use the Cell Density Calculator effectively:</p>
            
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Prepare Your Cell Sample</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Dilute your cell suspension appropriately</li>
                  <li>Count cells using a hemocytometer or cell counter</li>
                  <li>Record the dilution factor</li>
                  <li>Note any clumping or debris</li>
                </ul>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Cell Count Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Enter the number of cells counted</li>
                  <li>Input the dilution factor used</li>
                  <li>Select the appropriate cell type</li>
                  <li>Click the "Calculate Cell Density" button</li>
                </ul>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Review the calculated cell density</li>
                  <li>Check the viability percentage</li>
                  <li>Verify the total cell count</li>
                  <li>Use the density chart for visual analysis</li>
                  <li>Compare results with optimal ranges</li>
                </ul>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Important Considerations</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use appropriate dilution factors to avoid counting errors</li>
                  <li>Consider cell clumping when counting</li>
                  <li>Verify cell viability using trypan blue or equivalent</li>
                  <li>Check for contamination in your sample</li>
                  <li>Use the density chart to identify unusual patterns</li>
                  <li>Consider sample volume effects on accuracy</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Understanding Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Understanding Cell Density</h2>
            <p className="text-gray-600 mb-6">
              Cell density is a measure of the number of cells in a given volume. Understanding cell density is crucial for:
            </p>
            <ul className="list-none space-y-4">
              <li className="flex items-center">
                <span className="w-6 h-6 rounded-full bg-green-500 mr-3"></span>
                <strong className="font-medium">Cell Culture:</strong> Maintaining optimal growth conditions
              </li>
              <li className="flex items-center">
                <span className="w-6 h-6 rounded-full bg-blue-500 mr-3"></span>
                <strong className="font-medium">Experimental Setup:</strong> Ensuring consistent cell numbers across experiments
              </li>
              <li className="flex items-center">
                <span className="w-6 h-6 rounded-full bg-yellow-500 mr-3"></span>
                <strong className="font-medium">Quality Control:</strong> Monitoring cell growth and viability
              </li>
            </ul>
            <p className="italic text-gray-500 mt-6">
              Note: Regular calibration of counting equipment and consistent technique are essential for reproducible results.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CellDensityCalculator;